/**
 * Cadencya Analytics Service
 * 
 * Wrapper centralizado para Google Analytics 4.
 * Solo envía eventos si el usuario aceptó cookies de analytics.
 * 
 * Uso:
 *   import { trackEvent, trackSignUp, trackLogin, ... } from '../services/analytics';
 *   trackEvent('button_click', { button_name: 'cta_hero' });
 */

const GA_MEASUREMENT_ID = 'G-9BXNMNYT28';

// Verificar si gtag está disponible y el consentimiento fue dado
const isAnalyticsEnabled = () => {
  if (typeof window.gtag !== 'function') return false;
  try {
    const consent = localStorage.getItem('cadencya_cookie_consent');
    if (!consent) return false;
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
};

// ─── Evento genérico ───────────────────────────────────────────
export const trackEvent = (eventName, params = {}) => {
  if (!isAnalyticsEnabled()) return;
  window.gtag('event', eventName, params);
};

// ─── Pageview manual (para SPAs con rutas dinámicas) ───────────
export const trackPageView = (path, title) => {
  if (!isAnalyticsEnabled()) return;
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
  });
};

// ─── Autenticación ─────────────────────────────────────────────
export const trackSignUp = (method, userType) => {
  trackEvent('sign_up', {
    method,           // 'email'
    user_type: userType, // 'BANDA' | 'COFRADIA'
  });
};

export const trackLogin = (method) => {
  trackEvent('login', { method }); // 'email'
};

export const trackLogout = () => {
  trackEvent('logout');
};

// ─── Registro: selección de tipo ───────────────────────────────
export const trackUserTypeSelection = (userType) => {
  trackEvent('select_user_type', {
    user_type: userType, // 'BANDA' | 'COFRADIA'
  });
};

// ─── Perfil ────────────────────────────────────────────────────
export const trackProfileUpdate = (userType, fields) => {
  trackEvent('profile_update', {
    user_type: userType,
    fields_updated: Array.isArray(fields) ? fields.join(',') : fields,
  });
};

export const trackProfileCompletion = (userType, percentage) => {
  trackEvent('profile_completion', {
    user_type: userType,
    completion_percentage: percentage,
  });
};

export const trackAvatarUpload = (userType) => {
  trackEvent('avatar_upload', { user_type: userType });
};

export const trackPhotoUpload = (userType, count) => {
  trackEvent('photo_upload', {
    user_type: userType,
    photo_count: count,
  });
};

export const trackVerificationRequest = (userType) => {
  trackEvent('verification_request', { user_type: userType });
};

// ─── Búsqueda ──────────────────────────────────────────────────
export const trackSearch = (searchTerm, filters = {}) => {
  trackEvent('search', {
    search_term: searchTerm,
    ...filters,
  });
};

export const trackProfileView = (profileType, profileId) => {
  trackEvent('view_profile', {
    profile_type: profileType, // 'banda' | 'cofradia'
    profile_id: profileId,
  });
};

// ─── Contacto y conversiones ───────────────────────────────────
export const trackContactClick = (contactType, source) => {
  trackEvent('contact_click', {
    contact_type: contactType, // 'phone', 'email', 'whatsapp'
    source,                     // 'profile_detail', 'search_result'
  });
};

export const trackCTAClick = (ctaName, location) => {
  trackEvent('cta_click', {
    cta_name: ctaName,     // 'hero_busco_banda', 'hero_soy_banda', 'cta_crear_cuenta'
    location,               // 'hero', 'call_to_action', 'header'
  });
};

// ─── Mensajería ────────────────────────────────────────────────
export const trackMessageSent = (userType) => {
  trackEvent('message_sent', { user_type: userType });
};

export const trackConversationStarted = (userType) => {
  trackEvent('conversation_started', { user_type: userType });
};

// ─── Eventos ───────────────────────────────────────────────────
export const trackEventCreated = (userType) => {
  trackEvent('event_created', { user_type: userType });
};

export const trackEventResponse = (userType, response) => {
  trackEvent('event_response', {
    user_type: userType,
    response, // 'accepted', 'rejected', 'pending'
  });
};

// ─── Cookies ───────────────────────────────────────────────────
export const trackCookieConsent = (analytics, marketing) => {
  // Este siempre se envía si gtag existe (no depende de consentimiento previo,
  // ya que es el propio evento de dar consentimiento)
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', 'cookie_consent', {
    analytics_accepted: analytics,
    marketing_accepted: marketing,
  });
};
